import React, { useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import DashboardLayout from '../../components/DashboardLayout';
import axiosInstance from '../../utils/axiosInstance';

const TODAY = new Date().toISOString().split('T')[0];
const STATUS_VALUES = ['pending', 'completed'];

const STATUS_META = {
  pending: {
    label: 'Pending',
    badge: 'bg-orange-100 text-orange-800',
    accent: 'border-orange-200',
    focus: 'focus:ring-orange-500',
  },
  completed: {
    label: 'Completed',
    badge: 'bg-green-100 text-green-800',
    accent: 'border-green-200',
    focus: 'focus:ring-green-500',
  },
};

const toInputDate = (value) => {
  if (!value) return '';
  const asString = typeof value === 'string' ? value.trim() : value;
  const ymdPattern = /^\d{4}-\d{2}-\d{2}$/;
  if (typeof asString === 'string' && ymdPattern.test(asString)) return asString;

  const date = new Date(asString);
  if (Number.isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatDisplayDate = (value) => {
  if (!value) return 'No due date';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const isOverdue = (dueDate, status) => {
  if (!dueDate || status === 'completed') return false;
  const date = new Date(dueDate);
  if (Number.isNaN(date.getTime())) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return date < today;
};

export default function TodoList() {
  const [todos, setTodos] = useState([]);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDate, setSelectedDate] = useState(TODAY);
  const [dateFilterMode, setDateFilterMode] = useState('specific');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedTodo, setSelectedTodo] = useState(null);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  const [formData, setFormData] = useState({
    task_name: '',
    description: '',
    due_date: '',
    status: 'pending',
  });

  const hasActiveFilters =
    filterStatus !== 'all' || Boolean(searchQuery.trim()) || (dateFilterMode === 'specific' && Boolean(selectedDate));

  const resetFilters = () => {
    setFilterStatus('all');
    setSearchQuery('');
    setDateFilterMode('all');
    setSelectedDate(TODAY);
  };

  const filteredTodos = useMemo(() => {
    return todos
      .filter((todo) => {
        if (dateFilterMode === 'specific' && selectedDate) {
          if (!todo.due_date) return false;
          return toInputDate(todo.due_date) === selectedDate;
        }
        return true;
      })
      .filter((todo) => {
        if (filterStatus === 'all') return true;
        return todo.status === filterStatus;
      })
      .filter((todo) => {
        if (!searchQuery.trim()) return true;
        const query = searchQuery.toLowerCase();
        return (
          todo.task_name.toLowerCase().includes(query) ||
          (todo.description && todo.description.toLowerCase().includes(query))
        );
      });
  }, [todos, dateFilterMode, selectedDate, filterStatus, searchQuery]);

  const totalTasks = todos.length;
  const pendingCount = todos.filter((t) => t.status === 'pending').length;
  const completedCount = todos.filter((t) => t.status === 'completed').length;
  const overdueCount = todos.filter((t) => isOverdue(t.due_date, t.status)).length;

  const nextUpcomingTodo = useMemo(() => {
    const withTimestamps = todos
      .filter((todo) => todo.status === 'pending' && todo.due_date)
      .map((todo) => ({
        todo,
        timestamp: new Date(todo.due_date).getTime(),
      }))
      .filter(({ timestamp }) => !Number.isNaN(timestamp));

    if (withTimestamps.length === 0) return null;
    withTimestamps.sort((a, b) => a.timestamp - b.timestamp);
    return withTimestamps[0].todo;
  }, [todos]);

  const summaryCards = useMemo(() => {
    const cards = [
      {
        label: 'Total Tasks',
        value: totalTasks,
        meta: `${pendingCount} pending`,
        accent: 'from-blue-500 via-indigo-500 to-purple-500',
        icon: (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7h18M3 12h18M3 17h18" />
          </svg>
        ),
      },
      {
        label: 'Completed',
        value: completedCount,
        meta: `${pendingCount} remaining`,
        accent: 'from-emerald-500 to-green-600',
        icon: (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        ),
      },
      {
        label: 'Overdue',
        value: overdueCount,
        meta: overdueCount === 0 ? 'All clear' : 'Needs attention',
        accent: 'from-red-500 to-orange-500',
        icon: (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        ),
      },
    ];

    if (nextUpcomingTodo) {
      cards.push({
        label: 'Next Due',
        value: formatDisplayDate(nextUpcomingTodo.due_date),
        meta: nextUpcomingTodo.task_name,
        accent: 'from-purple-500 via-fuchsia-500 to-pink-500',
        icon: (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3" />
          </svg>
        ),
      });
    }

    return cards;
  }, [totalTasks, pendingCount, completedCount, overdueCount, nextUpcomingTodo]);

  useEffect(() => {
    const loadTodos = async () => {
      setLoading(true);
      try {
        const response = await axiosInstance.get('/admin/todos');
        setTodos(response?.data?.todos || []);
      } catch (err) {
        console.error('Error loading todos', err);
      } finally {
        setLoading(false);
      }
    };

    loadTodos();
  }, []);

  const openCreateModal = () => {
    setModalMode('create');
    setFormData({
      task_name: '',
      description: '',
      due_date: '',
      status: 'pending',
    });
    setErrors({});
    setSelectedTodo(null);
    setIsModalOpen(true);
  };

  const openEditModal = (todo) => {
    setModalMode('edit');
    setFormData({
      task_name: todo.task_name,
      description: todo.description || '',
      due_date: toInputDate(todo.due_date),
      status: todo.status,
    });
    setErrors({});
    setSelectedTodo(todo);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedTodo(null);
    setFormData({
      task_name: '',
      description: '',
      due_date: '',
      status: 'pending',
    });
    setErrors({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrors({});

    try {
      const payload = {
        ...formData,
        due_date: formData.due_date || null,
      };
      let response;
      if (modalMode === 'create') {
        response = await axiosInstance.post('/admin/todos', payload);
      } else if (selectedTodo) {
        response = await axiosInstance.put(`/admin/todos/${selectedTodo.id}`, payload);
      }

      if (response?.data?.success) {
        const todosResponse = await axiosInstance.get('/admin/todos');
        setTodos(todosResponse?.data?.todos || []);
        closeModal();
      }
    } catch (error) {
      if (error?.response?.data?.errors) {
        setErrors(error.response.data.errors);
      } else {
        console.error('Error submitting form', error);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleStatus = async (todo) => {
    const newStatus = todo.status === 'pending' ? 'completed' : 'pending';
    try {
      await axiosInstance.put(`/admin/todos/${todo.id}/status`, { status: newStatus });
      setTodos((prev) => prev.map((t) => (t.id === todo.id ? { ...t, status: newStatus } : t)));
    } catch (error) {
      console.error('Error updating status', error);
    }
  };

  const deleteTodo = async (id) => {
    const result = await Swal.fire({
      title: 'Delete Task?',
      text: 'This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, delete it',
    });

    if (!result.isConfirmed) return;

    try {
      await axiosInstance.delete(`/admin/todos/${id}`);
      setTodos((prev) => prev.filter((t) => t.id !== id));
      Swal.fire({
        title: 'Deleted',
        text: 'Task removed successfully.',
        icon: 'success',
        timer: 1400,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error('Error deleting todo', error);
      Swal.fire({
        title: 'Failed',
        text: 'Could not delete the task.',
        icon: 'error',
      });
    }
  };

  return (
    <DashboardLayout title="To Do List">
      <div className="mx-auto max-w-7xl space-y-6 px-4 py-6 md:px-6">
        <section className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-900 via-indigo-900 to-blue-800 text-white shadow-xl">
          <div className="absolute inset-0 opacity-50">
            <div className="absolute -right-10 -top-10 h-48 w-48 rounded-full bg-white/20 blur-3xl" />
            <div className="absolute bottom-0 left-10 h-40 w-40 rounded-full bg-blue-500/30 blur-3xl" />
          </div>
          <div className="relative z-10 flex flex-col gap-6 p-6 md:flex-row md:items-center md:justify-between md:p-10">
            <div className="max-w-2xl">
              <p className="text-xs font-semibold uppercase tracking-[0.4em] text-white/70">Task Console</p>
              <h1 className="mt-3 text-3xl font-bold md:text-4xl">Stay on top of your priorities</h1>
              <p className="mt-3 text-base text-white/80">
                {pendingCount === 0
                  ? 'Everything is wrapped up. Great job!'
                  : `You have ${pendingCount} active ${pendingCount === 1 ? 'task' : 'tasks'} waiting for your attention.`}
              </p>
              <div className="mt-6 flex flex-wrap items-center gap-3">
                <button
                  onClick={openCreateModal}
                  className="inline-flex items-center gap-2 rounded-xl bg-white/95 px-5 py-2.5 text-sm font-semibold text-slate-900 transition hover:bg-white"
                >
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Add Task
                </button>
                {nextUpcomingTodo && (
                  <div className="flex items-center gap-2 rounded-xl border border-white/30 bg-white/10 px-4 py-2 text-sm text-white/90">
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3" />
                    </svg>
                    <span>
                      Next due: <span className="font-semibold">{formatDisplayDate(nextUpcomingTodo.due_date)}</span>
                    </span>
                  </div>
                )}
              </div>
            </div>
            <div className="flex w-full flex-col gap-3 rounded-2xl border border-white/20 bg-white/5 p-5 md:w-auto">
              <div className="flex items-center gap-3 text-sm text-white/80">
                <span className="h-2 w-2 rounded-full bg-emerald-300" />
                Updated {formatDisplayDate(TODAY)}
              </div>
              <div className="text-4xl font-bold">{totalTasks}</div>
              <p className="text-sm text-white/70">Total tasks tracked across your workspace</p>
            </div>
          </div>
        </section>

        <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          {summaryCards.map((card, index) => (
            <div key={`${card.label}-${index}`} className="relative overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
              <div className={`absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r ${card.accent}`} />
              <div className="p-5">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-gray-500">{card.label}</p>
                  <div className="rounded-full bg-gray-50 p-2 text-gray-600">{card.icon}</div>
                </div>
                <p className="mt-3 text-3xl font-semibold text-gray-900">{card.value}</p>
                <p className="mt-2 text-sm text-gray-500">{card.meta}</p>
              </div>
            </div>
          ))}
        </section>

        <section className="rounded-2xl border border-gray-100 bg-white shadow-sm">
          <div className="flex flex-col gap-6 p-5">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Filters</h2>
                <p className="text-sm text-gray-500">Refine the list by status, time frame, or keywords</p>
              </div>
              {hasActiveFilters && (
                <button
                  type="button"
                  onClick={resetFilters}
                  className="inline-flex items-center gap-2 text-sm font-medium text-blue-600 hover:text-blue-700"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Reset filters
                </button>
              )}
            </div>

            <div className="flex flex-col gap-4 md:flex-row">
              <div className="w-full md:w-1/2">
                <label className="text-xs font-semibold uppercase tracking-wide text-gray-400">Search</label>
                <div className="mt-2 flex items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 px-4 py-2 focus-within:border-blue-500">
                  <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35m0 0A7 7 0 1010.65 5.65a7 7 0 006 10.998z" />
                  </svg>
                  <input
                    type="text"
                    placeholder="Search by title or description"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="flex-1 border-0 bg-transparent text-sm text-gray-900 placeholder:text-gray-400 focus:ring-0"
                  />
                </div>
              </div>

              <div className="w-full md:w-1/2">
                <label className="text-xs font-semibold uppercase tracking-wide text-gray-400">Date</label>
                <div className="mt-2 flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={() => setDateFilterMode('all')}
                    className={`rounded-xl px-4 py-2 text-sm font-medium transition ${
                      dateFilterMode === 'all'
                        ? 'bg-blue-600 text-white shadow-sm'
                        : 'border border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    All dates
                  </button>
                  <button
                    type="button"
                    onClick={() => setDateFilterMode('specific')}
                    className={`rounded-xl px-4 py-2 text-sm font-medium transition ${
                      dateFilterMode === 'specific'
                        ? 'bg-blue-600 text-white shadow-sm'
                        : 'border border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Specific date
                  </button>
                  {dateFilterMode === 'specific' && (
                    <input
                      type="date"
                      value={selectedDate}
                      onChange={(e) => setSelectedDate(e.target.value)}
                      className="rounded-xl border border-gray-200 px-4 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                    />
                  )}
                </div>
              </div>
            </div>

            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-gray-400">Status</label>
              <div className="mt-2 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => setFilterStatus('all')}
                  className={`rounded-xl px-4 py-2 text-sm font-medium transition ${
                    filterStatus === 'all'
                      ? 'bg-gray-900 text-white shadow-sm'
                      : 'border border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                  }`}
                >
                  All
                </button>
                {STATUS_VALUES.map((value) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setFilterStatus(value)}
                    className={`rounded-xl px-4 py-2 text-sm font-medium transition ${
                      filterStatus === value
                        ? 'bg-gray-900 text-white shadow-sm'
                        : 'border border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    {STATUS_META[value]?.label || value}
                  </button>
                ))}
              </div>
            </div>

            {hasActiveFilters && (
              <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-blue-50 bg-blue-50/60 px-4 py-3 text-sm text-blue-700">
                <span className="font-semibold">Showing {filteredTodos.length} {filteredTodos.length === 1 ? 'task' : 'tasks'}</span>
                <span className="text-blue-500">Filters applied</span>
              </div>
            )}
          </div>
        </section>

        <section className="rounded-2xl border border-gray-100 bg-white shadow-sm">
          <div className="flex flex-col gap-1 border-b border-gray-100 px-6 py-5 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Task timeline</h2>
              <p className="text-sm text-gray-500">{filteredTodos.length} {filteredTodos.length === 1 ? 'entry' : 'entries'} after filters</p>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <span className="flex items-center gap-1">
                <span className="h-2 w-2 rounded-full bg-orange-400" /> Pending
              </span>
              <span className="flex items-center gap-1">
                <span className="h-2 w-2 rounded-full bg-green-500" /> Completed
              </span>
            </div>
          </div>

          {loading ? (
            <div className="space-y-4 p-6">
              {[0, 1, 2].map((index) => (
                <div key={index} className="animate-pulse rounded-2xl border border-gray-100 bg-gray-50 p-5">
                  <div className="h-4 w-1/3 rounded bg-gray-200" />
                  <div className="mt-3 h-3 w-2/3 rounded bg-gray-200" />
                  <div className="mt-4 flex gap-3">
                    <div className="h-3 w-24 rounded bg-gray-200" />
                    <div className="h-3 w-16 rounded bg-gray-200" />
                  </div>
                </div>
              ))}
            </div>
          ) : filteredTodos.length === 0 ? (
            <div className="px-6 py-16 text-center">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-blue-50 text-blue-500">
                <svg className="h-7 w-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3" />
                </svg>
              </div>
              <h3 className="mt-6 text-lg font-semibold text-gray-900">No tasks found</h3>
              <p className="mt-2 text-sm text-gray-500">
                {dateFilterMode === 'specific' && selectedDate
                  ? `No tasks scheduled for ${formatDisplayDate(selectedDate)}`
                  : searchQuery
                  ? 'Try adjusting your search keywords'
                  : 'Create a new task to start tracking your work'}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {filteredTodos.map((todo) => {
                const statusMeta = STATUS_META[todo.status] || STATUS_META.pending;
                const overdue = isOverdue(todo.due_date, todo.status);
                return (
                  <article
                    key={todo.id}
                    className={`flex flex-col gap-4 border-l-4 ${statusMeta.accent} p-5 transition hover:bg-gray-50 md:flex-row md:items-center md:justify-between`}
                  >
                    <div className="flex flex-1 items-start gap-4">
                      <button
                        onClick={() => toggleStatus(todo)}
                        className={`flex h-11 w-11 items-center justify-center rounded-2xl border-2 transition ${
                          todo.status === 'completed'
                            ? 'border-green-500 bg-green-500 text-white'
                            : 'border-gray-200 text-gray-400 hover:border-green-500 hover:text-green-500'
                        }`}
                        title="Toggle status"
                      >
                        {todo.status === 'completed' ? (
                          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        ) : (
                          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v12m6-6H6" />
                          </svg>
                        )}
                      </button>

                      <div className="flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <h3 className={`text-lg font-semibold text-gray-900 ${todo.status === 'completed' ? 'line-through text-gray-500' : ''}`}>
                            {todo.task_name}
                          </h3>
                          <span className={`rounded-full px-3 py-1 text-xs font-semibold ${statusMeta.badge}`}>
                            {statusMeta.label}
                          </span>
                          <span className="text-xs text-gray-400">#{todo.id}</span>
                        </div>
                        {todo.description && (
                          <p className="mt-2 text-sm text-gray-600">{todo.description}</p>
                        )}
                        <div className="mt-3 flex flex-wrap items-center gap-3 text-sm">
                          <span
                            className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 ${
                              overdue
                                ? 'border-red-200 bg-red-50 text-red-700'
                                : 'border-gray-200 bg-gray-50 text-gray-600'
                            }`}
                          >
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            {todo.due_date ? formatDisplayDate(todo.due_date) : 'No due date'}
                            {overdue && <span className="font-semibold">(overdue)</span>}
                          </span>
                          {todo.description && (
                            <span className="inline-flex items-center gap-1 rounded-full border border-gray-200 px-3 py-1 text-xs text-gray-500">
                              <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7h18M3 12h18M3 17h18" />
                              </svg>
                              {todo.description.length} chars
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 self-start md:self-auto">
                      <button
                        onClick={() => openEditModal(todo)}
                        className="rounded-xl border border-gray-200 p-2 text-gray-400 transition hover:border-blue-200 hover:bg-blue-50 hover:text-blue-600"
                        title="Edit"
                      >
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => deleteTodo(todo.id)}
                        className="rounded-xl border border-gray-200 p-2 text-gray-400 transition hover:border-red-200 hover:bg-red-50 hover:text-red-600"
                        title="Delete"
                      >
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </section>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">
                {modalMode === 'create' ? 'Create New Task' : 'Edit Task'}
              </h2>
              <button
                onClick={closeModal}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Task Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.task_name}
                    onChange={(e) => setFormData({ ...formData, task_name: e.target.value })}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      errors.task_name ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter task name"
                  />
                  {errors.task_name && (
                    <p className="mt-1 text-sm text-red-600">{errors.task_name[0]}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={4}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter task description"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                  <input
                    type="date"
                    value={formData.due_date}
                    onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  {errors.due_date && (
                    <p className="mt-1 text-sm text-red-600">{errors.due_date[0]}</p>
                  )}
                </div>

                {modalMode === 'edit' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      {STATUS_VALUES.map((value) => (
                        <option key={value} value={value}>
                          {value.charAt(0).toUpperCase() + value.slice(1)}
                        </option>
                      ))}
                    </select>
                    {errors.status && (
                      <p className="mt-1 text-sm text-red-600">{errors.status[0]}</p>
                    )}
                  </div>
                )}
              </div>

              <div className="flex items-center justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Saving...' : modalMode === 'create' ? 'Create Task' : 'Update Task'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
